import React, { useState } from 'react';
import { MagicImage } from '@media-magic/sdk';
import type { ImageMetadata } from '../../types/image';

interface ImageCardProps {
  image: ImageMetadata;
  onDelete?: (mediaId: string) => void;
  onView?: (image: ImageMetadata) => void;
}

/**
 * Image card component for displaying image thumbnail and metadata
 */
export function ImageCard({ image, onDelete, onView }: ImageCardProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && window.confirm('Are you sure you want to delete this image?')) {
      onDelete(image.mediaId);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatSize = (bytes: number) => {
    if (!bytes || isNaN(bytes)) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get filename from originalFilename or extract from originalKey
  const filename = image.originalFilename ||
    (image.originalKey ? image.originalKey.split('/').pop() : image.mediaId);

  return (
    <div
      className="image-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onView?.(image)}
      style={{
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'var(--surface-1)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: isHovered ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isHovered
          ? '0 4px 12px rgba(0,0,0,0.15)'
          : '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          aspectRatio: '1',
          position: 'relative',
          backgroundColor: 'var(--line-soft)',
        }}
      >
        {/* Loading placeholder */}
        {!imageLoaded && !imageError && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--line-soft)',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                border: '3px solid var(--line-strong)',
                borderTopColor: 'var(--primary-600)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        {!imageError ? (
          <MagicImage
            src={image.mediaId}
            alt={filename}
            layout="fill"
            autoSize={true}
            objectFit="cover"
            crop
            autofocus="smart"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            style={{
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.2s ease-in',
            }}
            loading="lazy"
            showStats={true}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-400)',
            }}
          >
            Image unavailable
          </div>
        )}

        {/* Detection badges */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {image.boundingBoxes && image.boundingBoxes.length > 0 && (
            <div
              style={{
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
              }}
            >
              {image.boundingBoxes.length} face{image.boundingBoxes.length !== 1 ? 's' : ''}
            </div>
          )}
          {image.objects && image.objects.length > 0 && (
            <div
              style={{
                backgroundColor: 'rgba(25, 118, 210, 0.85)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
              }}
            >
              {image.objects.length} object{image.objects.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Hover overlay */}
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              padding: '8px',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView?.(image);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: 'var(--ink-900)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              View Details
            </button>
            {onDelete && (
              <button
                onClick={handleDelete}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--danger-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {/* Metadata */}
      <div
        style={{
          padding: '12px',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: '4px',
          }}
          title={filename}
        >
          {filename}
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--ink-600)',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>{image.width} x {image.height}</span>
          <span>{formatSize(image.size)}</span>
        </div>
        <div
          style={{
            fontSize: '11px',
            color: 'var(--ink-400)',
            marginTop: '4px',
          }}
        >
          {formatDate(image.uploadDate)}
        </div>
      </div>
    </div>
  );
}

export default ImageCard;
